/**
 * Hero Homepage Text Animation
 * Rotates text based on video timestamps using class-based visibility
 */

class HeroTextAnimation {
  constructor() {
    this.video = null;
    this.headingElement = null;
    this.nameElement = null;
    this.positionElement = null;
    this.descriptionElement = null;
    this.ctaElement = null;
    this.currentIndex = 0;
    this.animationFrameId = null;

    // Text rotation data with timestamps (in seconds)
    this.textData = [
      {
        timestamp: 0,
        heading: 'fitness founders',
        name: 'Nik Hecht,',
        position: 'Founder of Evolve Fitness & HYROX Training Club',
      },
      {
        timestamp: 7.8,
        heading: 'wellness leaders',
        name: 'Danny Davis,',
        position: 'Pro Fighter Turned Elite Coach',
      },
      {
        timestamp: 16.5,
        heading: 'gym owners',
        name: 'Emily Couture,',
        position: 'Co-Owner of Xtreme Couture',
      },
      {
        timestamp: 23.4,
        heading: 'business builders',
        name: 'Joe Tepe,',
        position: 'Founder of The Strip CrossFit, Tread Vegas, and The Engine Room',
      },
      {
        timestamp: 31.1,
        heading: 'martial arts leaders',
        name: 'Rob Wade,',
        position: '2nd-degree Black Belt and World-Class Instructor',
      },
      {
        timestamp: 39.7,
        heading: 'trainers and coaches',
        name: 'Robyn Sutton,',
        position: 'Personal Trainer and Bootcamp Coach',
      },
    ];

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupElements());
    } else {
      this.setupElements();
    }
  }

  setupElements() {
    // Get video element
    this.video = document.getElementById('js-hero-video');

    // Get individual elements for push animation
    this.headingSpans = document.querySelectorAll(
      '#js-hero-heading-container span'
    );
    this.detailItems = document.querySelectorAll('.c-home-hero__details__item');

    if (!this.video || !this.headingSpans.length || !this.detailItems.length) {
      console.warn('Hero animation: Required elements not found');
      return;
    }

    // Handle loop manually to prevent re-download on loop
    this.video.addEventListener('ended', () => {
      this.video.currentTime = 0;
      this.video.play();
    });

    // Initialize push animation system
    this.initializePushAnimation();

    // Add resize listener to recalculate widths
    this.setupResizeListener();

    this.animate();
  }

  setupResizeListener() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.recalculateContainerWidth();
      }, 150); // Debounce resize events
    });
  }

  recalculateContainerWidth() {
    if (this.headingSpans.length === 0) return;

    const container = document.getElementById('js-hero-heading-container');
    const loadingBar = document.getElementById('js-hero-loading-bar');
    let maxWidth = 0;

    // Temporarily make all spans visible to measure their widths
    this.headingSpans.forEach((span, index) => {
      span.style.position = 'static';
      span.style.opacity = '1';
      span.style.visibility = 'visible';
      span.style.display = 'block';
    });

    // Force a reflow and then measure
    container.offsetHeight;

    this.headingSpans.forEach((span, index) => {
      const width = span.offsetWidth;
      maxWidth = Math.max(maxWidth, width);
    });

    // Restore all spans to absolute positioning and reset styles
    this.headingSpans.forEach((span, index) => {
      span.style.position = 'absolute';
      span.style.visibility = '';
      span.style.display = '';
      span.classList.remove('is-active', 'push-in', 'push-out');
      if (index === this.currentIndex) {
        span.classList.add('is-active');
      }
    });

    if (maxWidth > 0) {
      container.style.width = maxWidth + 'px';
      container.style.minWidth = maxWidth + 'px';
    }

    // Reset loading bar if it's currently active to avoid backwards animation
    if (loadingBar && loadingBar.classList.contains('is-loading')) {
      const activeSpan = this.headingSpans[this.currentIndex];
      if (activeSpan) {
        // Reset the loading bar completely and restart it
        loadingBar.classList.remove('is-loading');
        loadingBar.style.removeProperty('width');
        loadingBar.style.setProperty('--loading-duration', '0s');

        // Force reflow
        loadingBar.offsetHeight;

        // Restart with new width immediately (no delay since text is already visible)
        const textWidth = activeSpan.offsetWidth;
        loadingBar.style.setProperty('--text-width', `${textWidth}px`);
        loadingBar.style.setProperty('--loading-duration', '3s');
        loadingBar.classList.add('is-loading');
      }
    }
  }

  animate() {
    if (!this.video) return;

    const currentTime = this.video.currentTime;
    const videoDuration = this.video.duration;

    // Handle video loop
    const loopedTime = videoDuration
      ? currentTime % videoDuration
      : currentTime;

    // Find the appropriate text based on timestamp with better precision
    let targetIndex = 0;
    for (let i = this.textData.length - 1; i >= 0; i--) {
      if (loopedTime >= this.textData[i].timestamp) {
        targetIndex = i;
        break;
      }
    }

    // Handle smooth restart when video loops back to beginning
    if (
      this.currentIndex === this.textData.length - 1 &&
      targetIndex === 0 &&
      loopedTime < 2
    ) {
      this.handleSmoothRestart();
    } else if (targetIndex !== this.currentIndex) {
      this.updateText(targetIndex);
    }

    this.currentIndex = targetIndex;

    // Continue animation loop
    this.animationFrameId = requestAnimationFrame(() => this.animate());
  }

  initializePushAnimation() {
    // Set all elements to default hidden state
    this.headingSpans.forEach((span, index) => {
      span.className = index === 0 ? 'is-active' : '';
    });

    this.detailItems.forEach((item, index) => {
      item.classList.remove('is-active', 'push-in', 'push-out');
      if (index === 0) {
        item.classList.add('is-active');
      }
    });

    // Ensure the container has proper width by measuring all spans and using the largest
    if (this.headingSpans.length > 0) {
      const container = document.getElementById('js-hero-heading-container');
      let maxWidth = 0;

      // Temporarily make all spans visible to measure their widths
      this.headingSpans.forEach((span, index) => {
        span.style.position = 'static';
        span.style.opacity = '1';
        span.style.visibility = 'visible';
        span.style.display = 'block';
      });

      // Force a reflow and then measure
      container.offsetHeight;

      this.headingSpans.forEach((span, index) => {
        const width = span.offsetWidth;
        maxWidth = Math.max(maxWidth, width);
      });

      // Restore all spans to absolute positioning and reset styles
      this.headingSpans.forEach((span, index) => {
        span.style.position = 'absolute';
        span.style.visibility = '';
        span.style.display = '';
        span.classList.remove('is-active', 'push-in', 'push-out');
        if (index === 0) {
          span.classList.add('is-active');
        }
      });

      if (maxWidth > 0) {
        container.style.width = maxWidth + 'px';
        container.style.minWidth = maxWidth + 'px';
      }
    }

    // Start loading bar for the initial segment
    const firstSegmentDuration =
      this.textData[1].timestamp - this.textData[0].timestamp;
    this.startLoadingBar(firstSegmentDuration, this.headingSpans[0]);
  }

  updateText(index) {
    // Only update if index actually changed
    if (index === this.currentIndex) return;

    const data = this.textData[index];
    if (!data) return;

    // Calculate duration for this segment
    const nextIndex = (index + 1) % this.textData.length;
    const nextTimestamp =
      nextIndex === 0
        ? this.video.duration
        : this.textData[nextIndex].timestamp;
    const segmentDuration = nextTimestamp - data.timestamp;

    // Get current and new elements
    const currentHeading = this.headingSpans[this.currentIndex];
    const newHeading = this.headingSpans[index];
    const currentDetail = this.detailItems[this.currentIndex];
    const newDetail = this.detailItems[index];

    // Animate heading push effect first
    this.pushElement(currentHeading, newHeading);

    // Start loading bar for this segment with the new active span
    this.startLoadingBar(segmentDuration, newHeading);

    // Animate details push effect with slight delay to avoid conflicts
    setTimeout(() => {
      this.pushElement(currentDetail, newDetail);
    }, 50);
  }

  startLoadingBar(duration, activeSpan) {
    const loadingBar = document.getElementById('js-hero-loading-bar');
    if (!loadingBar || !activeSpan) return;

    // Reset loading bar completely
    loadingBar.classList.remove('is-loading');
    loadingBar.style.removeProperty('width');
    loadingBar.style.setProperty('--loading-duration', '0s');

    // Force reflow to ensure reset is applied
    loadingBar.offsetHeight;

    // Start loading bar after push-in animation completes (1.2s delay)
    setTimeout(() => {
      const textWidth = activeSpan.offsetWidth;
      loadingBar.style.setProperty('--text-width', `${textWidth}px`);
      loadingBar.style.setProperty('--loading-duration', '3s');
      loadingBar.classList.add('is-loading');
    }, 1200);
  }

  pushElement(currentElement, newElement) {
    if (!newElement) return;

    const isHeading =
      currentElement?.parentElement?.id === 'js-hero-heading-container';
    const elementList = isHeading ? this.headingSpans : this.detailItems;

    elementList.forEach((el) => {
      el.classList.remove('is-active', 'push-in', 'push-out');
    });

    if (currentElement && currentElement !== newElement) {
      currentElement.classList.add('push-out');
    }

    newElement.classList.add('push-in');

    setTimeout(() => {
      elementList.forEach((el) => {
        el.classList.remove('is-active', 'push-in', 'push-out');
        if (el === newElement) {
          el.classList.add('is-active');
        }
      });
    }, 1250);
  }

  handleSmoothRestart() {
    const lastHeading = this.headingSpans[this.textData.length - 1];
    const firstHeading = this.headingSpans[0];
    const lastDetail = this.detailItems[this.textData.length - 1];
    const firstDetail = this.detailItems[0];

    this.pushElement(lastHeading, firstHeading);
    this.pushElement(lastDetail, firstDetail);

    const firstSegmentDuration =
      this.textData[1].timestamp - this.textData[0].timestamp;
    this.startLoadingBar(firstSegmentDuration, firstHeading);
  }

  fadeElement(element, newText) {
    if (element.textContent.trim() === newText.trim()) {
      return;
    }

    element.style.transition = 'opacity 0.2s ease-out';
    element.style.opacity = '0';

    setTimeout(() => {
      element.textContent = newText;
      element.style.opacity = '1';
      element.style.transition = 'opacity 0.4s ease-in';
    }, 200);
  }

  fadeStaticElement(element) {
    element.style.transition = 'opacity 0.2s ease-out';
    element.style.opacity = '0';

    setTimeout(() => {
      element.style.opacity = '1';
      element.style.transition = 'opacity 0.4s ease-in';
    }, 200);
  }

  destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}

// Initialize the animation when the module loads
new HeroTextAnimation();